import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, openDmsFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { DmsFileActions } from "@/components/common/DmsFileActions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { money, dateFmt } from "@/lib/format";
import { User, Pencil, Send, Lock, FileText } from "lucide-react";
import { toast } from "sonner";

const BAND_FAMILY = {
  "Outstanding Performer": "green", "Strong Performer": "blue", "Average Performer": "amber",
  "Needs Improvement": "amber", "Unsatisfactory": "red",
};
const LOCKED = ["designation", "department_id", "base_salary", "employment_type"];

export default function MySpace() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const { data: emp, isLoading } = useQuery({ queryKey: ["employee", me?.id], queryFn: () => get(`/hrms/employees/${me.id}`), enabled: !!me });
  const { data: payroll = [] } = useQuery({ queryKey: ["payroll-mine", me?.id], queryFn: () => get(`/hrms/payroll?employee_id=${me.id}`), enabled: !!me });
  const { data: history = [] } = useQuery({ queryKey: ["perf-history", me?.id], queryFn: () => get(`/hrms/performance/employee/${me.id}/history`), enabled: !!me });
  const { data: docs = [] } = useQuery({ queryKey: ["dms-files", "employee", me?.id], queryFn: () => get(`/dms/files?entity_type=employee&entity_id=${me.id}`), enabled: !!me });
  const { data: leave } = useQuery({ queryKey: ["leave"], queryFn: () => get("/hrms/leave") });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  useEffect(() => { if (emp && !editing) setForm(emp); }, [emp, editing]);
  const [requesting, setRequesting] = useState(false);
  const [reqForm, setReqForm] = useState({});

  const saveProfile = useMutation({
    mutationFn: (body) => patch(`/users/${me.id}`, { ...body, _actor: me?.id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["employee", me.id] }); toast.success("Profile updated"); setEditing(false); },
  });
  const requestChange = useMutation({
    mutationFn: (fields) => post("/hrms/profile-requests", { employee_id: me.id, requested_fields: fields }),
    onSuccess: () => { toast.success("Change request submitted for HR approval"); setRequesting(false); setReqForm({}); },
  });

  if (isLoading || !emp || !form) return <Skeleton className="h-96" />;
  const myLeave = (leave?.applications || []).filter((a) => a.employee_id === me.id);
  const bal = leave?.balances.find((b) => b.employee_id === me.id)?.used || {};

  return (
    <div>
      <PageHeader title="My HR" subtitle="Your profile, payslips, performance and leave — all in one place." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Section title="Profile" action={!editing && (
          <Button size="sm" variant="outline" data-testid="myspace-edit" onClick={() => setEditing(true)}><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
        )}>
          <div className="grid grid-cols-2 gap-4 p-4">
            {[["phone", "Phone"], ["address", "Address"]].map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                {editing ? <Input data-testid={`myspace-${k}`} className="mt-1" value={form[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
                  : <div className="mt-0.5 text-sm font-medium">{emp[k] || "—"}</div>}
              </div>
            ))}
            {["designation", "department"].map((k) => (
              <div key={k}>
                <Label className="flex items-center gap-1 capitalize"><Lock className="h-3 w-3" />{k}</Label>
                <div className="mt-0.5 text-sm font-medium">{k === "department" ? emp.department?.name : emp[k] || "—"}</div>
              </div>
            ))}
          </div>
          {editing && (
            <div className="flex justify-end gap-2 border-t border-border/60 p-4">
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm(emp); }}>Cancel</Button>
              <Button size="sm" data-testid="myspace-save" onClick={() => saveProfile.mutate({ phone: form.phone, address: form.address })}>Save</Button>
            </div>
          )}
        </Section>

        <Section title="Request a Change" action={!requesting && (
          <Button size="sm" variant="outline" data-testid="myspace-request-change" onClick={() => setRequesting(true)}><Send className="mr-1.5 h-3.5 w-3.5" />New Request</Button>
        )}>
          <div className="p-4 text-xs text-muted-foreground">
            Designation, department, employment type and salary require HR approval to change.
          </div>
          {requesting && (
            <div className="space-y-3 border-t border-border/60 p-4">
              {LOCKED.map((f) => (
                <div key={f}>
                  <Label className="capitalize">{f.replace("_", " ")}</Label>
                  <Input data-testid={`req-${f}`} className="mt-1" placeholder="Leave blank to skip" value={reqForm[f] ?? ""} onChange={(e) => setReqForm((r) => ({ ...r, [f]: e.target.value }))} />
                </div>
              ))}
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setRequesting(false)}>Cancel</Button>
                <Button size="sm" data-testid="submit-profile-request" onClick={() => {
                  const fields = Object.fromEntries(Object.entries(reqForm).filter(([, v]) => v !== ""));
                  if (!Object.keys(fields).length) { toast.error("Fill in at least one field"); return; }
                  requestChange.mutate(fields);
                }}>Submit Request</Button>
              </div>
            </div>
          )}
        </Section>

        <Section title="Performance History">
          {!history.length ? <EmptyState icon={User} title="No reviews yet" /> : (
            <div className="divide-y divide-border">
              {history.map((h) => (
                <div key={h.cycle_id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span>{h.cycle_name}</span>
                  <div className="flex items-center gap-2"><span className="font-medium">{h.combined_pct}%</span><StatusBadge family={BAND_FAMILY[h.band]} label={h.band} /></div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Payslips">
          {!payroll.length ? <EmptyState icon={FileText} title="No payslips yet" /> : (
            <div className="divide-y divide-border">
              {payroll.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span>{p.month}</span>
                  <div className="flex items-center gap-2"><span className="font-medium">{money(p.net)}</span><StatusBadge status={p.status} /></div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Leave" action={<Button size="sm" variant="outline" onClick={() => navigate("/hrms/leave/apply")}>Apply Leave</Button>}>
          {!myLeave.length ? <EmptyState icon={User} title="No leave applications" /> : (
            <div className="divide-y divide-border">
              {myLeave.map((a) => (
                <div key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span>{a.type} · {dateFmt(a.from)} – {dateFmt(a.to)}</span>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          )}
          {leave && (
            <div className="grid grid-cols-3 gap-2 border-t border-border/60 p-4 text-xs text-muted-foreground">
              {leave.types.filter((t) => t.paid !== false).map((t) => (
                <div key={t.id}>{t.name}: <span className="font-medium text-foreground">{t.total - (bal[t.id] || 0)}</span> left</div>
              ))}
            </div>
          )}
        </Section>

        <Section title="My Documents">
          {!docs.length ? <EmptyState icon={FileText} title="No documents on file" /> : (
            <div className="divide-y divide-border">
              {docs.filter((d) => d.visibility !== "hr_only").map((d) => (
                <div key={d.id} data-testid={`myspace-doc-${d.id}`} onClick={() => openDmsFile(d.id)}
                  className="group flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left text-sm hover:bg-muted/50">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span>{d.name}</span></div>
                  <DmsFileActions file={d} queryKey={["dms-files", "employee", me?.id]} canConvert={can("hrms", "edit")} canDelete={can("hrms", "delete")} />
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
